// EditInNavbarMenu — "Edit in…" menu (Open in Editor / Edit in Code / Open in
// Sidebar) from the selected-component navbar. Migrated 2026-06-09 (task #2)
// from the legacy DropdownParentFocus (which stayed visible when inactive) to
// @symbo.ls/dropdown: EditorTools' IconButton opens this body via
// el.call('openDropdown', { content: { extends: 'EditInNavbarMenu' } }); the
// plugin owns placement + outside-click/ESC dismiss, which fixes the
// visible-when-inactive bug. Each row dismisses via closeDropdown.
//
// Portal note: the item handlers call canvas-element methods (setCanvasScope /
// getWindow / setWindow). getCanvas() resolves via getRootData('Canvas') first
// (global), so those work even though @symbo.ls/dropdown renders this body in a
// workspace-level portal outside the canvas subtree.
//
// The four functions below stay exported at module scope for
// EditInNavbarMenu.test.js's offline Node coverage (it imports and calls
// them directly with a mock `el`) — they're ALSO duplicated into
// EditInNavbarMenu's own `scope` below for use inside its serialized
// factories (el.scope only resolves within that component's own tree, and a
// bare module-scope reference reads as undefined once frank.toJSON's the
// component for platform delivery — workspace d6ea1142,
// project_frank_drops_module_local_helpers). `MENU_ID` has no external
// consumer, so it moves into `scope` outright rather than staying duplicated.

export const resolveCanvasV2CodeTarget = (el) => {
  const canvasElement = el.call('getCanvas')
  const canvasRoot = canvasElement?.state?.root
  const project = canvasRoot?.data || el.getRootState('data')
  const canvas = canvasRoot?.canvas || el.call('getCanvasV2')
  const selected = el.call('getSelected')
  const selectedState = selected?.state?.parse?.() || {}
  const key = canvas?.selection?.primary || el.call('getSelectedKey') || selectedState.key
  const selectedType = selectedState.type || selectedState._cvType
  const type =
    selectedType && project?.[selectedType]?.[key]
      ? selectedType
      : Object.hasOwn(project?.components || {}, key)
        ? 'components'
        : Object.hasOwn(project?.pages || {}, key)
          ? 'pages'
          : null

  return {
    key,
    type,
    value: type ? project?.[type]?.[key] : null,
    selectedState
  }
}

export const isCanvasV2EditorContext = (el) => {
  const canvasElement = el.call('getCanvas')
  const canvas = canvasElement?.state?.root?.canvas || el.call('getCanvasV2')
  return Boolean(canvas?.selection && Array.isArray(canvas.selection.ids))
}

export const getCanvasV2CodeEditor = (el) => {
  const canvasElement = el.call('getCanvas')
  return (
    canvasElement?.parent?.CanvasEditor?.CanvasV2CodeEditor ||
    el.getRoot()?.lookdown?.('CanvasV2CodeEditor') ||
    null
  )
}

// NOT a DOMQL prop — a plain exported function EditInNavbarMenu.test.js
// imports and calls directly. Its resolveCanvasV2CodeTarget/getCanvasV2CodeEditor
// closure below is ordinary JS and resolves fine for that (non-serialized)
// caller; the DUPLICATE in EditInNavbarMenu's own `scope` above is what the
// component's actual onClick handler calls.
export const openCanvasV2CodeEditor = (el) => {
  const { type, key, value, selectedState } = resolveCanvasV2CodeTarget(el)
  const codeEditor = getCanvasV2CodeEditor(el)
  if (!type || !key || value == null || !codeEditor) return false

  // Do not use setCanvasScope() here. It calls the legacy syncCanvas() path,
  // which activates the full-screen Chosen scene and leaves v2 interactions
  // blocked after the panel closes.
  const scope = el.getCanvasScope()
  if (scope) {
    scope.editorActive = false
    scope.preferredEditor = 'code'
  }
  codeEditor.data.itemType = type
  codeEditor.data.itemKey = key
  codeEditor.setNodeStyles({ display: 'flex' })
  codeEditor.state.set(
    {
      ...selectedState,
      type,
      key,
      code: null,
      value: el.call('deepClone', value)
    },
    { forceMonacoUpdate: true }
  )
  return true
}

export const EditInNavbarMenu = {
  // FA204 / frank trap — see the header comment above the module-scope
  // exports for the full rationale. openCanvasV2CodeEditor calls its
  // siblings via `this.<name>` (object-literal properties can't see each
  // other as bare identifiers — only `el.scope.<name>()` binds `this` to
  // the scope bag).
  scope: {
    MENU_ID: 'editInNavbar',
    resolveCanvasV2CodeTarget(el) {
      const canvasElement = el.call('getCanvas')
      const canvasRoot = canvasElement?.state?.root
      const project = canvasRoot?.data || el.getRootState('data')
      const canvas = canvasRoot?.canvas || el.call('getCanvasV2')
      const selected = el.call('getSelected')
      const selectedState = selected?.state?.parse?.() || {}
      const key = canvas?.selection?.primary || el.call('getSelectedKey') || selectedState.key
      const selectedType = selectedState.type || selectedState._cvType
      const type =
        selectedType && project?.[selectedType]?.[key]
          ? selectedType
          : Object.hasOwn(project?.components || {}, key)
            ? 'components'
            : Object.hasOwn(project?.pages || {}, key)
              ? 'pages'
              : null

      return {
        key,
        type,
        value: type ? project?.[type]?.[key] : null,
        selectedState
      }
    },
    isCanvasV2EditorContext(el) {
      const canvasElement = el.call('getCanvas')
      const canvas = canvasElement?.state?.root?.canvas || el.call('getCanvasV2')
      return Boolean(canvas?.selection && Array.isArray(canvas.selection.ids))
    },
    getCanvasV2CodeEditor(el) {
      const canvasElement = el.call('getCanvas')
      return (
        canvasElement?.parent?.CanvasEditor?.CanvasV2CodeEditor ||
        el.getRoot()?.lookdown?.('CanvasV2CodeEditor') ||
        null
      )
    },
    openCanvasV2CodeEditor(el) {
      const { type, key, value, selectedState } = this.resolveCanvasV2CodeTarget(el)
      const codeEditor = this.getCanvasV2CodeEditor(el)
      if (!type || !key || value == null || !codeEditor) return false

      // Do not use setCanvasScope() here. It calls the legacy syncCanvas()
      // path, which activates the full-screen Chosen scene and leaves v2
      // interactions blocked after the panel closes.
      const scope = el.getCanvasScope()
      if (scope) {
        scope.editorActive = false
        scope.preferredEditor = 'code'
      }
      codeEditor.data.itemType = type
      codeEditor.data.itemKey = key
      codeEditor.setNodeStyles({ display: 'flex' })
      codeEditor.state.set(
        {
          ...selectedState,
          type,
          key,
          code: null,
          value: el.call('deepClone', value)
        },
        { forceMonacoUpdate: true }
      )
      return true
    }
  },
  // Body-only menu for the shared DropdownRoot — no position / outside-click
  // here; the plugin owns placement + dismiss. Keeps its own navbar glass card.
  minWidth: '210px',
  flow: 'y',
  gap: '0',
  padding: 'Y Z1',
  round: 'radiusControl',
  theme: 'navbar',
  role: 'menu',
  ariaLabel: (el) => el.call('polyglot', 'editor.editIn.label'),
  shadow: 'overlay',

  ListInDropdown: {
    margin: '- -Z2',
    gap: '0',
    childProps: {
      ':hover': {
        '& svg': {
          opacity: 1
        },
        // House dropdown hover wash — the brand DropdownItem's own hover is the
        // near-invisible gray6.05, so override it with the `hover` token to
        // match every other menu (DESIGN_LANGUAGE.md §4.3).
        background: 'hover'
      },
      padding: 'Z Z2',
      fontSize: 'A',
      fontWeight: '400',
      round: 'radiusControl',
      Text: null,
      color: 'title',
      Icon: {
        color: 'currentColor',
        opacity: '1',
        order: '-1',
        margin: '- Y1 - -',
        fontWeight: '100'
      }
    },
    childrenAs: 'props',
    // On a v2 board only the rows whose actions actually EXIST there are
    // offered. "Open in Editor" activates the legacy full-screen Chosen scene
    // (setCanvasScope editorActive — the very flow the openCanvasV2CodeEditor
    // comment above warns leaves v2 interactions blocked) and "Open in
    // Sidebar" opens the v1 file-explorer window that v2 chrome never mounts
    // — both dead-or-harmful clicks on v2 (2026-08-08 per Nika "icons
    // clicking and functionality"). v1 surfaces keep all three rows.
    children: (el) => {
      const v2 = el.scope.isCanvasV2EditorContext(el)
      const rows = [
        {
          v1Only: true,
          text: '{{ editor.editIn.openEditor | polyglot }}',
          Icon: {
            name: 'playOvalDotted'
          },
          onClick: (ev, el, s) => {
            el.call('closeDropdown', el.scope.MENU_ID)
            el.setCanvasScope({
              editorActive: true,
              preferredEditor: 'editor'
            })
            el.call('openEditor')
          }
        },
        {
          text: '{{ editor.editIn.editCode | polyglot }}',
          Icon: {
            name: 'code'
          },
          onClick: (ev, el, s) => {
            el.call('closeDropdown', el.scope.MENU_ID)
            // Canvas v2 is the default. A portal-local isCanvasV2() lookup can
            // be false even while this toolbar belongs to a v2 artboard, so only
            // enter the legacy Chosen flow when legacy mode was explicitly set.
            if (el.getCanvasScope()?.canvasV2 !== false) {
              el.scope.openCanvasV2CodeEditor(el)
              return
            }
            if (el.getWindow('componentEditor') === 'code') {
              el.closeWindow('componentEditor')
            } else {
              el.setWindow('componentEditor', 'code')
            }
            el.setCanvasScope({
              preferredEditor: 'code'
            })
          }
        },
        {
          v1Only: true,
          text: '{{ editor.editIn.openSidebar | polyglot }}',
          Icon: {
            name: 'sidebarLeftFill'
          },
          onClick: async (ev, el, s) => {
            el.call('closeDropdown', el.scope.MENU_ID)
            if (el.getWindow('explorer') !== 'explorer') {
              await el.setWindow('explorer', 'explorer')
            }
            setTimeout(() => {
              const itemFound = el.call('findFileSidebarItem', s.type, s.key)
              itemFound?.state?.update({
                showLayers: true
              })
            }, 350)
            el.setCanvasScope({
              preferredEditor: 'sidebar'
            })
          }
        }
      ]
      return (v2 ? rows.filter((r) => !r.v1Only) : rows).map(({ v1Only, ...row }) => row)
    }
  }
}
